import styled, { css } from 'styled-components';

export const Page = styled.form`
  display: grid;
  gap: 14px;
  padding-bottom: 72px;
  font-size: 12px;
  color: #3f4254;
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  p {
    margin: 2px 0 0;
    color: #7e8299;
    font-size: 12px;
  }
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-top: 1px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  background: #fff;
  color: #3f4254;

  &:hover {
    background: #f5f8fa;
  }
`;

export const Card = styled.section`
  padding: 16px 18px;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0px 0px 17px -10px rgba(122, 122, 122, 1);
`;

export const CardTitle = styled.h6`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #3f4254;

  small {
    margin-left: 6px;
    font-weight: 400;
    color: #7e8299;
  }
`;

export const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols ?? 3}, minmax(0, 1fr));
  gap: 12px 16px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Span = styled.div<{ $span?: number }>`
  grid-column: span ${({ $span }) => $span ?? 1};

  @media (max-width: 992px) {
    grid-column: span ${({ $span }) => Math.min($span ?? 1, 2)};
  }

  @media (max-width: 576px) {
    grid-column: span 1;
  }
`;

export const Field = styled.label<{ $invalid?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  min-width: 0;
  text-align: left;

  > span.field-label {
    font-size: 11px;
    font-weight: 500;
    color: #5e6278;

    em {
      color: #dc2229;
      font-style: normal;
      margin-left: 2px;
    }
  }

  input,
  textarea,
  select {
    width: 100%;
    font-size: 13px;
    line-height: 1.4;
    padding: var(--input-padding-sm, 4px 10px);
    min-height: var(--input-height-sm, 32px);
    border-radius: 6px;
    border: 1px solid
      ${({ $invalid }) => ($invalid ? '#ef4444' : 'var(--color-input-border)')};
    background: #fff;
    color: #181c32;
    transition: all 0.15s ease;

    &::placeholder {
      font-size: 12px;
      color: var(--color-input-placeholder);
    }

    &:hover:not(:focus):not(:disabled) {
      border-color: ${({ $invalid }) => ($invalid ? '#ef4444' : '#b5bcc9')};
    }

    &:focus {
      outline: none;
      border-color: ${({ $invalid }) =>
        $invalid ? '#ef4444' : 'var(--color-input-border-focus, #009ef7)'};
      box-shadow: 0 0 0 3px
        ${({ $invalid }) =>
          $invalid ? 'rgba(239, 68, 68, 0.12)' : 'rgba(0, 158, 247, 0.12)'};
    }

    &:disabled {
      background: #f5f8fa;
      color: #a1a5b7;
    }
  }

  textarea {
    resize: vertical;
    min-height: 64px;
  }
`;

const hintColors = {
  error: '#dc2229',
  info: '#7e8299',
  success: '#1a9e5c',
  warning: '#b07a00',
};

export const Hint = styled.small<{ $tone?: keyof typeof hintColors }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  line-height: 1.35;
  color: ${({ $tone }) => hintColors[$tone ?? 'info']};
`;

export const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 7px;
  background: #f5f8fa;
  border: 1px solid #eff2f5;
  flex-wrap: wrap;
`;

export const SegmentButton = styled.button<{ $active: boolean }>`
  border: 0;
  border-radius: 5px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  color: #5e6278;
  transition: all 0.15s ease;

  ${({ $active }) =>
    $active &&
    css`
      background: #fff;
      color: #009ef7;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 28px;

  .group-label {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #5e6278;
    margin-bottom: 4px;
  }
`;

export const Chip = styled.label<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 6px 6px 0;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $checked }) => ($checked ? '#009ef7' : '#e4e6ef')};
  background: ${({ $checked }) => ($checked ? '#f1faff' : '#fff')};
  color: ${({ $checked }) => ($checked ? '#009ef7' : '#5e6278')};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;

  input {
    accent-color: #009ef7;
    margin: 0;
  }
`;

export const Banner = styled.div<{ $tone: 'error' | 'warning' | 'info' | 'success' }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 13px;
  border: 1px solid;

  ${({ $tone }) =>
    ({
      error: css`
        background: #fff5f8;
        border-color: #f9c6d2;
        color: #b5133c;
      `,
      warning: css`
        background: #fff8dd;
        border-color: #f6e2a1;
        color: #8a6100;
      `,
      info: css`
        background: #f1faff;
        border-color: #b8e2fb;
        color: #0b6aa2;
      `,
      success: css`
        background: #e8fff3;
        border-color: #b4ecd0;
        color: #1a7a4c;
      `,
    })[$tone]}

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 2px;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }

  button.link {
    border: 0;
    background: none;
    padding: 0;
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }
`;

export const ItemList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
`;

export const Item = styled.div<{ $editing?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid ${({ $editing }) => ($editing ? '#009ef7' : '#eff2f5')};
  background: ${({ $editing }) => ($editing ? '#f1faff' : '#fcfcfd')};
  font-size: 12px;

  strong {
    display: block;
    font-size: 13px;
    color: #181c32;
  }

  span {
    color: #7e8299;
  }
`;

export const ItemActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: #7e8299;

    &:hover {
      background: #eff2f5;
      color: #009ef7;
    }

    &.danger:hover {
      background: #fff5f8;
      color: #f1416c;
    }
  }
`;

export const Editor = styled.div`
  padding: 14px;
  border-radius: 6px;
  border: 1px dashed #b8e2fb;
  background: #fafdff;
`;

export const EditorActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px dashed #b5bcc9;
  background: #fff;
  color: #009ef7;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    border-color: #009ef7;
    background: #f1faff;
  }
`;

export const Empty = styled.p`
  margin: 0 0 10px;
  color: #a1a5b7;
  font-size: 12px;
`;

export const Footer = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -4px 17px -10px rgba(122, 122, 122, 1);

  .dirty {
    color: #7e8299;
    font-size: 12px;
  }

  .actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
  }
`;

export const Switch = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  input {
    width: 34px;
    height: 18px;
    cursor: pointer;
  }
`;
